import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../utils/logger';
import { useDataCache } from '../context/DataCacheContext';

const logger = createLogger({ module: 'useCompetencyEvidence' });

export interface CompetencyEvidence {
  id: string;
  focus_id: string;
  competency_id: string;
  career_card_id?: string;
  evidence_id?: string;
  evidence_type?: 'career_card' | 'note';
  impact_summary?: string;
  relevance_score?: number;
  user_id?: string;
  created_at: string;
  // Joined data
  competency?: {
    name: string;
    description: string;
    weight: number;
  };
  evidence?: {
    title: string;
    content?: string;
    created_at: string;
  };
}

interface UseCompetencyEvidenceProps {
  focusId?: string;
  competencyId?: string;
  evidenceId?: string;
}

export const useCompetencyEvidence = (props?: UseCompetencyEvidenceProps) => {
  const { user } = useAuth();
  const { getData, setData: setCacheData } = useDataCache();
  const [evidence, setEvidence] = useState<CompetencyEvidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = `competency-evidence-${props?.focusId || 'all'}-${props?.competencyId || 'all'}-${props?.evidenceId || 'all'}`;

  const fetchEvidence = async () => {
    if (!user) {
      logger.warn('No user found, skipping competency evidence fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cachedData = getData(cacheKey);
      if (cachedData && Array.isArray(cachedData)) {
        setEvidence(cachedData);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('competency_evidence')
        .select(`
          *,
          competency:performance_competencies!competency_evidence_competency_id_fkey (
            name,
            description,
            weight
          )
        `)
        .eq('user_id', user.id);

      // Apply filters if provided
      if (props?.focusId) {
        query = query.eq('focus_id', props.focusId);
      }
      if (props?.competencyId) {
        query = query.eq('competency_id', props.competencyId);
      }
      if (props?.evidenceId) {
        query = query.eq('evidence_id', props.evidenceId);
      }


      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Handle empty or null data
      if (!data || !Array.isArray(data)) {
        setEvidence([]);
        setCacheData(cacheKey, []);
        setLoading(false);
        return;
      }

      // Fetch evidence details
      const evidenceWithDetails = await Promise.all(
        data.map(async (item) => {
          let evidenceDetails = null;
          
          if (item.evidence_id) {
            if (item.evidence_type === 'career_card') {
              const { data: card } = await supabase
                .from('career_cards')
                .select('title, content, created_at')
                .eq('id', item.evidence_id)
                .single();
              evidenceDetails = card;
            } else if (item.evidence_type === 'note') {
              const { data: note } = await supabase
                .from('notes')
                .select('title, content, created_at')
                .eq('id', item.evidence_id)
                .single();
              evidenceDetails = note;
            }
          }

          return {
            ...item,
            evidence: evidenceDetails
          };
        })
      );

      setEvidence(evidenceWithDetails);
      setCacheData(cacheKey, evidenceWithDetails);
    } catch (error) {
      logger.error('Error fetching competency evidence:', error);
      setError('Failed to load competency evidence');
    } finally {
      setLoading(false);
    }
  };

  const addEvidence = async (competencyId: string, evidenceId: string, evidenceType: 'career_card' | 'note', focusId: string) => {
    if (!user) {
      throw new Error('User must be logged in to add evidence');
    }

    try {
      const { data, error } = await supabase
        .from('competency_evidence')
        .insert({
          competency_id: competencyId,
          evidence_id: evidenceId,
          evidence_type: evidenceType,
          focus_id: focusId,
          user_id: user.id,
          // For backwards compatibility
          career_card_id: evidenceType === 'career_card' ? evidenceId : null
        })
        .select()
        .single();

      if (error) throw error;

      // Invalidate cache
      setCacheData(cacheKey, null);
      await fetchEvidence();

      return data;
    } catch (error) {
      logger.error('Error adding competency evidence:', error);
      throw error;
    }
  };

  const removeEvidence = async (evidenceId: string) => {
    if (!user) {
      throw new Error('User must be logged in to remove evidence');
    }

    try {
      const { error } = await supabase
        .from('competency_evidence')
        .delete()
        .eq('id', evidenceId);

      if (error) throw error;

      // Invalidate cache
      setCacheData(cacheKey, null);
      await fetchEvidence();
    } catch (error) {
      logger.error('Error removing competency evidence:', error);
      throw error;
    }
  };

  const getCompetencyCoverage = (competencyId: string) => {
    if (!Array.isArray(evidence)) return 0;
    const competencyEvidence = evidence.filter(e => e.competency_id === competencyId);
    return competencyEvidence.length;
  };

  const getEvidenceByCompetency = (competencyId: string) => {
    if (!Array.isArray(evidence)) return [];
    return evidence.filter(e => e.competency_id === competencyId);
  };

  const getEvidenceByType = (evidenceType: string) => {
    if (!Array.isArray(evidence)) return [];
    return evidence.filter(e => e.evidence_type === evidenceType);
  };

  useEffect(() => {
    fetchEvidence();
  }, [user, props?.focusId, props?.competencyId, props?.evidenceId]);

  return {
    evidence,
    loading,
    error,
    addEvidence,
    removeEvidence,
    getCompetencyCoverage,
    getEvidenceByCompetency,
    getEvidenceByType,
    refreshEvidence: fetchEvidence
  };
};